import React, { useState, useEffect } from 'react';
import './App.css';
import Home from './components/Home';
import Calculator from './components/Calculator';
import Expenses from './components/Expenses';
import Comparison from './components/Comparison';
import DeliveryGuidelines from './components/DeliveryGuidelines';
import ProductionPackets from './components/ProductionPackets';
import ProductionSchedule from './components/ProductionSchedule';
import AdminUsers from './components/AdminUsers';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import QuickDeliveryEstimateDrawer from './components/QuickDeliveryEstimateDrawer';
import { fetchDeliveryJson } from './utils/deliveryApiFetch';
import { TAB_KEY, readView, writeView, clearViews } from './utils/viewMemory';

const DELIVERY_API_URL = process.env.REACT_APP_DELIVERY_API_URL || '';
const PROPOSALS_API_URL = process.env.REACT_APP_PROPOSALS_API_URL || process.env.REACT_APP_CLIENT_API_URL || '';
const SLACK_WEBHOOK_URL = process.env.REACT_APP_SLACK_WEBHOOK_URL || 'https://hooks.slack.com/services/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';
const SCHEDULE_API_URL = process.env.REACT_APP_SCHEDULE_API_URL || 'https://script.google.com/macros/s/AKfycbxtHs_qe1at7oQP6iGrnNCB7KOcAH_3MAlfOCglPYvVEcVGrGtnXoL1BzMH5govhgqm/exec';

// Admin allowlist (also enforced server-side). Add additional admins by
// either updating this list or setting the `ADMIN_EMAILS` Script Property
// on the Apps Script project (comma-separated, lowercased).
const ADMIN_EMAILS = ['megan@mayker.com'];
const isAdminEmail = (email) =>
  !!email && ADMIN_EMAILS.includes(String(email).trim().toLowerCase());

// Full-sheet getAll (Calculations + Expenses) is off by default so Production Packets
// and schedule work are not competing with a heavy portal read. Set to true to restore
// Expenses / Profitability nav and sheet history in the calculator.
const SHOW_EXPENSES_AND_PROFITABILITY =
  process.env.REACT_APP_SHOW_EXPENSES_PROFITABILITY === 'true';

// Authentication will be handled via Google Sheets

// Debug: Log environment variables
console.log('Environment variables loaded:');
console.log('REACT_APP_DELIVERY_API_URL:', DELIVERY_API_URL ? '✓ Set' : '✗ Not set');
console.log('REACT_APP_PROPOSALS_API_URL:', PROPOSALS_API_URL ? '✓ Set' : '✗ Not set');
console.log('PROPOSALS_API_URL value:', PROPOSALS_API_URL);
console.log('SLACK_WEBHOOK_URL:', SLACK_WEBHOOK_URL ? '✓ Set' : '✗ Not set');

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // The open tool survives a page refresh in this browser tab.
  const [activeTab, setActiveTab] = useState(() => readView(TAB_KEY) || 'home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [quickEstimateOpen, setQuickEstimateOpen] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [savedExpenses, setSavedExpenses] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState(
    sessionStorage.getItem('deliveryPortalEmail') || ''
  );
  const [currentUserToken, setCurrentUserToken] = useState(
    sessionStorage.getItem('deliveryPortalToken') || ''
  );
  const isAdmin = isAdminEmail(currentUserEmail);

  useEffect(() => { writeView(TAB_KEY, activeTab); }, [activeTab]);

  // Check if user is already logged in (from sessionStorage)
  useEffect(() => {
    const authStatus = sessionStorage.getItem('deliveryPortalAuth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
      setCurrentUserEmail(sessionStorage.getItem('deliveryPortalEmail') || '');
      setCurrentUserToken(sessionStorage.getItem('deliveryPortalToken') || '');
    }
  }, []);

  // Authentication handler - authenticates via Google Sheets
  const handleLogin = async (email, password) => {
    if (!DELIVERY_API_URL) {
      return { success: false, error: 'Authentication service not configured' };
    }

    try {
      let result = await fetchDeliveryJson(
        DELIVERY_API_URL,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({ action: 'login', email, password }),
          mode: 'cors'
        }
      );
      // Older web app deployments only supported GET login.
      if (result && result.error === 'Invalid action') {
        result = await fetchDeliveryJson(
          `${DELIVERY_API_URL}?action=login&email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
        );
      }

      if (result.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('deliveryPortalAuth', 'authenticated');
        sessionStorage.setItem('deliveryPortalToken', result.token || '');
        sessionStorage.setItem('deliveryPortalEmail', email);
        setCurrentUserEmail(email);
        setCurrentUserToken(result.token || '');
        
        // If user needs to change password, show alert
        if (result.needsPasswordChange) {
          alert('Please change your password using the "Forgot Your Password" link.');
        }
        
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Invalid email or password' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = error && error.message ? String(error.message) : '';
      const hint = detail.includes('Google error page')
        ? detail
        : 'Login is taking longer than usual or the service returned an invalid response. Wait a moment and try again.';
      return { success: false, error: hint };
    }
  };

  // Request password reset token
  const handleRequestPasswordReset = async (email) => {
    if (!DELIVERY_API_URL) {
      return { success: false, error: 'Authentication service not configured' };
    }

    try {
      const result = await fetchDeliveryJson(
        `${DELIVERY_API_URL}?action=requestPasswordReset&email=${encodeURIComponent(email)}`
      );
      return result;
    } catch (error) {
      console.error('Request password reset error:', error);
      return { success: false, error: 'Service unavailable. Please try again later.' };
    }
  };

  // Reset password with token
  const handleResetPassword = async (email, resetToken, newPassword) => {
    if (!DELIVERY_API_URL) {
      return { success: false, error: 'Authentication service not configured' };
    }

    try {
      const result = await fetchDeliveryJson(
        `${DELIVERY_API_URL}?action=resetPassword&email=${encodeURIComponent(email)}&resetToken=${encodeURIComponent(resetToken)}&newPassword=${encodeURIComponent(newPassword)}`
      );
      return result;
    } catch (error) {
      console.error('Reset password error:', error);
      return { success: false, error: 'Service unavailable. Please try again later.' };
    }
  };

  // Logout handler
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUserEmail('');
    setCurrentUserToken('');
    sessionStorage.removeItem('deliveryPortalAuth');
    sessionStorage.removeItem('deliveryPortalToken');
    sessionStorage.removeItem('deliveryPortalEmail');
    clearViews();
    setActiveTab('home');
  };

  const handleSaveCalculation = async (calculationData) => {
    // Save to localStorage immediately
    const existing = JSON.parse(localStorage.getItem('deliveryCalculations') || '[]');
    existing.push(calculationData);
    localStorage.setItem('deliveryCalculations', JSON.stringify(existing));
    setSavedCalculations(existing);

    // Also save to Google Sheets if API is configured
    if (DELIVERY_API_URL) {
      try {
        console.log('Sending to:', DELIVERY_API_URL);
        console.log('Payload:', JSON.stringify({
          action: 'saveCalculation',
          data: calculationData
        }));
        
        const response = await fetch(DELIVERY_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            action: 'saveCalculation',
            data: calculationData
          }),
          mode: 'cors'
        });
        
        if (!response.ok) {
          const text = await response.text();
          console.error('Response not OK:', response.status, text);
          throw new Error(`HTTP ${response.status}: ${text}`);
        }
        
        const result = await response.json();
        console.log('Response from server:', result);
        
        if (result.success) {
          console.log('Calculation saved to Google Sheets');
        } else {
          console.error('Server returned error:', result.error);
        }
      } catch (err) {
        console.error('Error saving to Google Sheets:', err);
        console.error('Error details:', err.message, err.stack);
      }
    }
  };

  // Send Slack notification for expense submission
  const sendSlackNotification = async (expenseData) => {
    if (!SLACK_WEBHOOK_URL) {
      console.warn('Slack webhook URL not configured');
      return;
    }

    try {
      // Format expense details for Slack message
      const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };

      const expenseDetails = [];
      
      // Lodging
      if (expenseData.lodging && expenseData.lodging.total > 0) {
        expenseDetails.push(`*Lodging:* ${formatCurrency(expenseData.lodging.total)} (${expenseData.lodging.rooms || 0} rooms, ${expenseData.lodging.nights || 0} nights)`);
      }
      
      // Gas
      if (expenseData.gas && expenseData.gas.total > 0) {
        expenseDetails.push(`*Gas:* ${formatCurrency(expenseData.gas.total)}`);
      }
      
      // Flights
      if (expenseData.flights && expenseData.flights.total > 0) {
        expenseDetails.push(`*Flights:* ${formatCurrency(expenseData.flights.total)} (${expenseData.flights.count || 0} flights)`);
      }
      
      // Per Diem
      if (expenseData.perDiem && expenseData.perDiem.total > 0) {
        const supervisors = expenseData.perDiem.supervisors != null
          ? expenseData.perDiem.supervisors
          : (expenseData.perDiem.salaried || 0);
        const teamMembers = expenseData.perDiem.teamMembers != null
          ? expenseData.perDiem.teamMembers
          : (expenseData.perDiem.hourly || 0);
        expenseDetails.push(`*Per Diem:* ${formatCurrency(expenseData.perDiem.total)} (${supervisors} supervisor${supervisors === 1 ? '' : 's'}, ${teamMembers} team member${teamMembers === 1 ? '' : 's'})`);
      }
      
      // Truck Rental
      if (expenseData.truckRental && expenseData.truckRental.total > 0) {
        expenseDetails.push(`*Truck Rental:* ${formatCurrency(expenseData.truckRental.total)} (${expenseData.truckRental.count || 0} trucks)`);
      }
      
      // Other Expenses
      if (expenseData.otherExpenses && expenseData.otherExpenses.length > 0) {
        const otherExpensesList = expenseData.otherExpenses
          .map(exp => `  • ${exp.name}: ${formatCurrency(exp.total)}`)
          .join('\n');
        expenseDetails.push(`*Other Expenses:*\n${otherExpensesList}`);
      }

      const expenseDetailsText = expenseDetails.length > 0 
        ? expenseDetails.join('\n') 
        : 'No expenses listed';

      const slackMessage = {
        text: `💰 New Expense Report Submitted`,
        blocks: [
          {
            type: 'header',
            text: {
              type: 'plain_text',
              text: '💰 New Expense Report Submitted'
            }
          },
          {
            type: 'section',
            fields: [
              {
                type: 'mrkdwn',
                text: `*Project Number:*\n${expenseData.projectNumber || 'N/A'}`
              },
              {
                type: 'mrkdwn',
                text: `*Client:*\n${expenseData.clientName || 'N/A'}`
              },
              {
                type: 'mrkdwn',
                text: `*Report Date:*\n${expenseData.reportDate || 'N/A'}`
              },
              {
                type: 'mrkdwn',
                text: `*Total Cost:*\n${formatCurrency(expenseData.totalCost || 0)}`
              }
            ]
          },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*Expense Details:*\n${expenseDetailsText}`
            }
          }
        ]
      };

      // Add team information if available
      if (expenseData.reportPreparedBy || expenseData.deliverySupervisor) {
        const teamInfo = [];
        if (expenseData.reportPreparedBy) {
          teamInfo.push(`*Prepared By:* ${expenseData.reportPreparedBy}`);
        }
        if (expenseData.deliverySupervisor) {
          teamInfo.push(`*Supervisor:* ${expenseData.deliverySupervisor}`);
        }
        if (expenseData.deliveryTeamMembers) {
          teamInfo.push(`*Team Members:* ${expenseData.deliveryTeamMembers}`);
        }

        slackMessage.blocks.push({
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: teamInfo.join('\n')
          }
        });
      }

      // Add notes if available
      if (expenseData.notes) {
        slackMessage.blocks.push({
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*Notes:*\n${expenseData.notes}`
          }
        });
      }

      // Send to Slack
      console.log('Sending Slack notification to:', SLACK_WEBHOOK_URL);
      console.log('Slack message payload:', JSON.stringify(slackMessage, null, 2));
      
      const response = await fetch(SLACK_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slackMessage)
      });

      // Slack webhooks return plain text "ok", not JSON
      const responseText = await response.text();
      console.log('Slack response status:', response.status);
      console.log('Slack response:', responseText);

      if (!response.ok) {
        throw new Error(`Slack API error: ${response.status} - ${responseText}`);
      }

      if (responseText !== 'ok') {
        console.warn('Slack webhook returned unexpected response:', responseText);
      }

      console.log('✅ Slack notification sent successfully');
    } catch (err) {
      console.error('❌ Error sending Slack notification:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        webhookUrl: SLACK_WEBHOOK_URL ? 'Set' : 'Not set'
      });
      // Don't throw - we don't want Slack failures to prevent expense saving
    }
  };

  const handleSaveExpense = async (expenseData) => {
    // Save to localStorage immediately
    const existing = JSON.parse(localStorage.getItem('deliveryExpenses') || '[]');
    existing.push(expenseData);
    localStorage.setItem('deliveryExpenses', JSON.stringify(existing));
    setSavedExpenses(existing);

    // Also save to Google Sheets if API is configured
    let savedToSheets = false;
    if (DELIVERY_API_URL) {
      try {
        const response = await fetch(DELIVERY_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            action: 'saveExpense',
            data: expenseData
          }),
          mode: 'cors'
        });
        const result = await response.json();
        if (result.success) {
          console.log('Expense saved to Google Sheets');
          savedToSheets = true;
        }
      } catch (err) {
        console.error('Error saving to Google Sheets:', err);
      }
    }

    // Slack notification is now handled by the Google Apps Script backend
    // No need to send from frontend (avoids CORS issues)
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Login 
        onLogin={handleLogin}
        onRequestPasswordReset={handleRequestPasswordReset}
        onResetPassword={handleResetPassword}
      />
    );
  }

  const navItems = [
    { key: 'home', label: 'Home' },
    { key: 'schedule', label: 'Daily Production Plan' },
    { key: 'packets', label: 'Production Packets' },
    { key: 'calculator', label: 'Delivery Fee Calculator' },
    ...(SHOW_EXPENSES_AND_PROFITABILITY
      ? [
        { key: 'expenses', label: 'Submit Expenses' },
        { key: 'comparison', label: 'Profitability Log' },
      ]
      : []),
    { key: 'guidelines', label: 'Delivery Guidelines' },
    ...(isAdmin ? [{ key: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <div className="app-shell">
      <header className="portal-header">
        <div className="portal-header-row">
          <div className="portal-header-left">
            <button
              type="button"
              className="portal-nav-toggle"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen(true)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="portal-brand">
              <img src="/mayker_icon-black.svg" alt="Mayker" className="brand-mark-header" />
              <span className="portal-title">Mayker Delivery Portal</span>
            </div>
          </div>
          <button type="button" className="portal-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="app-body">
        <Sidebar
          items={navItems}
          activeTab={activeTab}
          onSelect={setActiveTab}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <div className="app-main">
          <main className="container page">
            <div className="tab-content">
          {activeTab === 'home' && (
            <Home
              userEmail={currentUserEmail}
              scheduleApiUrl={SCHEDULE_API_URL}
              onNavigate={setActiveTab}
              onOpenQuickEstimate={() => setQuickEstimateOpen(true)}
            />
          )}
          {activeTab === 'schedule' && (
            <div>
              <h2 className="tab-headline">Daily Production Plan</h2>
              <p className="calculator-description">
                Build, review, and publish the daily production schedule.
              </p>
              <ProductionSchedule scheduleApiUrl={SCHEDULE_API_URL} />
            </div>
          )}
          {activeTab === 'calculator' && (
            <div className="calc-page">
              <header className="calc-pagehead">
                <div className="calc-pagehead__title-row">
                  <h2 className="tab-headline calc-pagehead__title">Delivery Fee Calculator</h2>
                  <div className="calc-pagehead__tools">
                    <button
                      type="button"
                      className="qe-header-link"
                      onClick={() => setQuickEstimateOpen(true)}
                      title="Calculate a planning estimate without selecting a project."
                    >
                      <span>Quick Estimate</span>
                      <svg
                        className="qe-header-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="calculator-description calc-pagehead__description">
                  Calculate delivery fees for individual projects or multi-event programs. Delivery estimates assume locations within 600 miles of headquarters. For long-distance or specialty deliveries, contact JP before quoting.
                </p>
              </header>
              <Calculator 
                onSaveCalculation={handleSaveCalculation}
                savedCalculations={savedCalculations}
                proposalsApiUrl={PROPOSALS_API_URL}
              />
            </div>
          )}
          {activeTab === 'expenses' && (
            <div>
              <h2 className="tab-headline">Submit Expenses</h2>
              <p className="calculator-description">
                Please submit all delivery fee-related expenses within 48 business hours of project or program completion. Submit for an individual project or for a multi-event program when costs are shared across linked events. This helps us track costs by market, project scope, and delivery complexity—so we can price accurately moving forward.
              </p>
              <Expenses 
                onSaveExpense={handleSaveExpense}
                savedCalculations={savedCalculations}
                savedExpenses={savedExpenses}
                proposalsApiUrl={PROPOSALS_API_URL}
              />
            </div>
          )}
          {activeTab === 'comparison' && (
            <div>
              <h2 className="tab-headline">Profitability Log</h2>
              <p className="calculator-description">
                This page serves as our historical log of executed events and provides a clear view of delivery fee performance. It compares delivery fees quoted and collected from clients against actual delivery-related expenses incurred, calculating profitability by event, market, and scope.
              </p>
              <Comparison 
                savedCalculations={savedCalculations}
                savedExpenses={savedExpenses}
                proposalsApiUrl={PROPOSALS_API_URL}
              />
            </div>
          )}
          {activeTab === 'packets' && (
            <div className="pp-page">
              <ProductionPackets
                deliveryApiUrl={DELIVERY_API_URL}
                userEmail={currentUserEmail}
              />
            </div>
          )}
          {activeTab === 'guidelines' && (
            <div>
              <h2 className="tab-headline">Delivery Guidelines</h2>
              <DeliveryGuidelines />
            </div>
          )}
          {activeTab === 'admin' && isAdmin && (
            <div>
              <h2 className="tab-headline">Admin</h2>
              <p className="calculator-description">
                Manage who can access the Delivery Portal. Adding a user creates their account with a
                temporary password ({'Welcome123!'}); they’ll be prompted to set their own on first login.
              </p>
              <AdminUsers
                deliveryApiUrl={DELIVERY_API_URL}
                userEmail={currentUserEmail}
                userToken={currentUserToken}
              />
            </div>
          )}
            </div>
          </main>
        </div>
      </div>

      {quickEstimateOpen && (
        <QuickDeliveryEstimateDrawer
          onClose={() => setQuickEstimateOpen(false)}
          onOpenCalculator={() => setActiveTab('calculator')}
        />
      )}
    </div>
  );
}

export default App;
